export const GUIDES = [
    {
        slug: "artificial-turf-cost-idaho",
        title: "How Much Does Artificial Turf Cost in Idaho? (2026 Guide)",
        description: "Real 2026 pricing for artificial turf installation in Boise, Meridian, and the Treasure Valley — per square foot costs, what affects price, and how turf pays for itself.",
        category: "Pricing",
        readTime: "6 min read",
        date: "2026-01-15",
        intro: "If you're pricing artificial turf in Idaho, you'll see numbers all over the map. Here's what Treasure Valley homeowners actually pay in 2026, what drives the price, and why most families break even faster than they expect.",
        sections: [
            {
                heading: "The short answer",
                paragraphs: [
                    "Most professionally installed artificial turf lawns in the Boise area cost between $9 and $15 per square foot, fully installed. A typical 800 sq ft backyard runs $7,200–$12,000 depending on turf grade, site access, and how much base work your yard needs.",
                    "That price includes excavation, the compacted base, the turf itself, seaming, edging, infill, and cleanup. Be cautious of quotes that exclude base work — in Idaho's freeze-thaw climate, the base is what separates a 15-year lawn from a 3-year headache.",
                ],
            },
            {
                heading: "What moves the price up or down",
                paragraphs: [
                    "Yard size and shape: larger, simpler rectangles cost less per square foot than small, curved, obstacle-filled yards. Access matters too — a yard we can reach with equipment is cheaper to prep than one that needs hand-carrying.",
                    "Turf grade: premium multi-tone turf with a thatch layer costs more than entry-level products, but it's the difference between 'obviously fake' and 'wait, that's turf?' Pet-specific and cool-tech upgrades add modest cost.",
                    "Existing conditions: removing sod is standard; removing rock, concrete, or failed DIY turf adds labor. Slopes and drainage corrections also add scope.",
                ],
            },
            {
                heading: "The break-even math most people miss",
                paragraphs: [
                    "An average Idaho lawn drinks 50,000+ gallons of water per year. Between water bills, mowing (or a mowing service), fertilizer, aeration, and reseeding, natural grass typically costs $1,500–$2,500 per year to maintain in the Treasure Valley.",
                    "Most homeowners break even in 4–7 years, and our turf carries a 16-year manufacturer warranty. After break-even, the lawn is effectively free — and you get your Saturdays back.",
                ],
            },
            {
                heading: "How to get an exact number",
                paragraphs: [
                    "Every yard is different, which is why we do free on-site measurements with flat, itemized quotes — no ranges, no surprises. It takes about 20 minutes and there's zero obligation.",
                ],
            },
        ],
        faq: [
            { q: "Is artificial turf worth the money in Idaho?", a: "For most Treasure Valley homeowners, yes. Between water savings ($800–$1,500/yr), eliminated mowing and fertilizer costs, and a 15–20 year lifespan, most lawns pay for themselves in 4–7 years." },
            { q: "What is the cheapest way to install artificial turf?", a: "DIY kits are cheapest upfront, but poor base work is the #1 cause of failed turf in Idaho's freeze-thaw climate. A properly built compacted base is what makes turf last 15+ years." },
        ],
    },
    {
        slug: "artificial-turf-vs-real-grass-idaho",
        title: "Artificial Turf vs. Real Grass in Idaho: An Honest Comparison",
        description: "Water use, maintenance, heat, cost, and looks — a straight-talk comparison of artificial turf vs natural grass for Treasure Valley homeowners.",
        category: "Guides",
        readTime: "7 min read",
        date: "2026-02-02",
        intro: "We install turf for a living, so you'd expect us to say turf wins. Instead, here's the honest version: where real grass still makes sense, where turf clearly wins, and the trade-offs nobody mentions.",
        sections: [
            {
                heading: "Water: the deciding factor in Idaho",
                paragraphs: [
                    "The Treasure Valley is high desert. Keeping 1,000 sq ft of Kentucky bluegrass alive through July takes roughly 25,000–35,000 gallons in a single summer — and water rates keep climbing.",
                    "Turf uses none. If water cost or conservation matters to you, this category isn't close.",
                ],
            },
            {
                heading: "Maintenance time",
                paragraphs: [
                    "A natural lawn asks for mowing (30+ times a year), edging, fertilizing, weed control, aeration, and spring/fall cleanup. Realistically, that's 60–80 hours a year.",
                    "Turf asks for an occasional rinse and a power-broom once or twice a year. Call it 2 hours a year.",
                ],
            },
            {
                heading: "Heat: the honest trade-off",
                paragraphs: [
                    "Turf gets warmer than grass in direct afternoon sun — that's real. Modern cool-tech infills keep our installs 10–15°F cooler than older products, and a 30-second hose rinse drops surface temperature instantly.",
                    "In practice: bare feet are fine mornings and evenings; on 100° afternoons, spray it down or wait for shade. For most families it's a non-issue; for south-facing yards with no shade, it's worth discussing at your estimate.",
                ],
            },
            {
                heading: "Looks and lifespan",
                paragraphs: [
                    "Real grass looks great in May and patchy by August unless you pour money into it. Quality turf looks identical every day of the year — including February.",
                    "Modern multi-tone turf is nearly indistinguishable from grass at standing distance, and it carries a 16-year warranty. Real grass carries no warranty at all.",
                ],
            },
            {
                heading: "Where real grass still wins",
                paragraphs: [
                    "If you love gardening, want a habitat lawn, or simply enjoy yard work — keep the grass. Large agricultural properties also don't pencil out for turf. For front yards, backyards, pet runs, and play areas under ~5,000 sq ft, turf usually wins on math alone.",
                ],
            },
        ],
        faq: [
            { q: "Does artificial turf get too hot for dogs in Idaho?", a: "Turf runs warmer than grass in direct sun. Cool-tech infill, light-colored turf blends, shade planning, and a quick rinse on hot afternoons keep it comfortable for paws." },
            { q: "Is turf bad for the environment?", a: "Turf eliminates watering, fertilizers, pesticides, and gas-mower emissions. Our products are PFAS-free. The trade-off is that turf is a synthetic product — which is why choosing PFAS-free, lab-tested materials matters." },
        ],
    },
    {
        slug: "how-long-does-artificial-turf-last",
        title: "How Long Does Artificial Turf Last in Idaho's Climate?",
        description: "Freeze-thaw cycles, high desert sun, and snow — here's how artificial turf holds up in Idaho and what makes an installation last 15–20 years.",
        category: "Guides",
        readTime: "5 min read",
        date: "2026-02-20",
        intro: "Idaho throws everything at a lawn: 100° summer sun, freeze-thaw winters, spring runoff, and the occasional hailstorm. Here's how quality turf handles all of it — and what actually determines whether your lawn lasts 5 years or 20.",
        sections: [
            {
                heading: "The realistic lifespan",
                paragraphs: [
                    "Quality residential artificial turf lasts 15–20 years in Idaho. Our turf carries a 16-year manufacturer warranty — one of the longest in the industry — and most lawns outlive it.",
                    "Putting greens often last even longer because the fibers are shorter and denser, with less blade to mat down over time.",
                ],
            },
            {
                heading: "What Idaho's climate does to turf",
                paragraphs: [
                    "UV exposure: high-desert sun is intense. Cheap turf fades to a blue-green within a few years; UV-stabilized premium turf holds its color. This is the single biggest material difference.",
                    "Freeze-thaw: this is where installation quality matters most. Water expands when it freezes, so a poorly built base heaves and creates lumps. A properly compacted, free-draining aggregate base shrugs off Idaho winters.",
                    "Snow: turf handles snow fine. Let it melt naturally, avoid metal shovels, and never use salt or ice melt on the surface.",
                ],
            },
            {
                heading: "The three things that determine lifespan",
                paragraphs: [
                    "1. Materials: UV-stabilized, multi-tone polyethylene fibers with a quality backing. 2. Base work: 3–4 inches of compacted crushed aggregate, graded for drainage. 3. Infill: the right type and depth keeps blades standing and protects the backing.",
                    "Get those three right and your lawn outlasts the warranty. Cut corners on any of them and you'll see ripples, seams, or fading within a few years.",
                ],
            },
            {
                heading: "Maintenance that adds years",
                paragraphs: [
                    "Rinse dusty or high-traffic areas occasionally, power-broom once or twice a year to lift the blades, and keep infill topped up in heavy-use spots. That's genuinely the whole list — about 2 hours per year.",
                ],
            },
        ],
        faq: [
            { q: "Does artificial turf fade in the sun?", a: "Cheap turf does. Premium UV-stabilized turf (what we install) is engineered for high-sun climates and carries long fade warranties — expect consistent color for the life of the lawn." },
            { q: "Can turf survive Idaho winters?", a: "Yes. Turf goes dormant-proof — snow and freezing don't harm quality turf. The base must be built correctly for freeze-thaw, which is why professional installation matters in Idaho." },
        ],
    },
    {
        slug: "is-artificial-turf-safe-for-dogs",
        title: "Is Artificial Turf Safe for Dogs? What Idaho Pet Owners Should Know",
        description: "PFAS, heat, drainage, odors, and digging — a complete safety guide to artificial turf for dogs, from a Treasure Valley installer.",
        category: "Pet Turf",
        readTime: "6 min read",
        date: "2026-03-10",
        intro: "About half our customers have dogs, and they all ask the same five questions: Is it safe? Will it smell? Can they dig it up? Does it get too hot? And how do you clean it? Here are straight answers.",
        sections: [
            {
                heading: "Safety: what 'pet-safe turf' actually means",
                paragraphs: [
                    "The materials matter. Some older or imported turfs contained lead, heavy metals, or PFAS ('forever chemicals'). We exclusively install turf that is certified PFAS-free and independently lab-tested for lead, heavy metals, phthalates, and VOCs.",
                    "Pet systems also use antimicrobial fibers, which inhibit the bacteria that cause odors and can irritate skin. For bare paws and bellies, this is the standard to insist on.",
                ],
            },
            {
                heading: "Drainage and odor control",
                paragraphs: [
                    "Urine doesn't pool on a proper pet installation — permeable backing and a free-draining base pass 30+ inches of water per hour straight through. Solid waste gets picked up as usual, and the spot rinses clean with a hose.",
                    "We add odor-neutralizing infill that traps ammonia before it smells. Most owners do a quick weekly rinse of their dog's favorite spots and that's it.",
                ],
            },
            {
                heading: "Digging, durability, and mud",
                paragraphs: [
                    "Pet installations use reinforced, secured edging so diggers can't get purchase on a corner. The turf itself stands up to scratching and zoomies far better than natural grass — no more worn paths along the fence line.",
                    "The mud benefit deserves its own mention: no dirt, no mud, no muddy paw prints from November through April. For most dog owners, this alone justifies the install.",
                ],
            },
            {
                heading: "Heat and summer comfort",
                paragraphs: [
                    "Any surface — turf, concrete, deck — gets warm in direct Idaho summer sun. Cool-tech infill keeps our turf meaningfully cooler, and a 30-second rinse drops the temperature immediately. Morning and evening play needs no adjustment at all.",
                ],
            },
        ],
        faq: [
            { q: "How do you clean dog urine off artificial turf?", a: "Urine drains straight through permeable backing. Rinse favorite spots with a hose weekly; for a deeper clean, use a pet-safe enzymatic turf cleaner. No scrubbing required." },
            { q: "Will turf hold up to two big dogs?", a: "Yes — pet-grade turf is designed for exactly this. Heavy-duty fibers, secured edges, and dense infill handle large, active dogs. Many of our installs are multi-dog households." },
        ],
    },
    {
        slug: "backyard-putting-green-cost-guide",
        title: "What Does a Backyard Putting Green Cost in Idaho?",
        description: "Real pricing for backyard putting greens in Boise and the Treasure Valley — size, contours, fringe, and what separates a $3k practice green from a $15k short-game complex.",
        category: "Pricing",
        readTime: "5 min read",
        date: "2026-03-28",
        intro: "A backyard putting green is the most requested upgrade we build for golfers — and the pricing range is wide because the possibilities are. Here's what drives the cost and what different budgets actually get you.",
        sections: [
            {
                heading: "Typical price ranges",
                paragraphs: [
                    "Custom putting greens in the Treasure Valley typically run $15–$25 per square foot installed. A simple 300 sq ft practice green with one or two cups usually lands in the $4,500–$7,500 range.",
                    "Larger greens with multiple tiers, contour breaks, fringe, and chipping aprons — the 600–1,000+ sq ft short-game setups — typically run $10,000–$20,000+ depending on complexity.",
                ],
            },
            {
                heading: "What drives the cost",
                paragraphs: [
                    "Size is the obvious one. After that: contours (sculpted breaks require more base shaping time), cup count, fringe and collar turf (a second turf type with its own base work), and site access.",
                    "The putting surface itself is a specialized turf — shorter, denser, and rolled to a specific speed. It's more expensive per square foot than lawn turf, and it should be: ball roll quality is the entire point.",
                ],
            },
            {
                heading: "What separates a great green from a carpet",
                paragraphs: [
                    "The base. A green is only as true as what's under it — we sculpt a compacted aggregate base to your designed contours, so breaks break the same way for decades.",
                    "Roll tuning. After installation we putt across every line and adjust infill until the surface rolls true at your preferred speed. Ask any installer whether they do this — it separates pros from landscapers with a roll of turf.",
                ],
            },
            {
                heading: "Is it worth it?",
                paragraphs: [
                    "If you practice 15 minutes a day, a home green replaces dozens of trips to the course per season — and it's usable roughly ten months a year in the Treasure Valley. It also photographs beautifully and is a genuine differentiator when you sell your home.",
                ],
            },
        ],
        faq: [
            { q: "How big should a backyard putting green be?", a: "For meaningful practice, 300–500 sq ft with 2–3 cups covers lag putting and short putts. Add fringe and a chipping area if you want full short-game work." },
            { q: "Can a putting green be built on a slope?", a: "Yes — slopes are where great greens come from. We shape the base to control drainage and create true, puttable contours regardless of your yard's grade." },
        ],
    },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
